/**
 * Team Feed Component
 * 
 * Team communication feed for cases - like a WhatsApp group.
 * Features:
 * - Real-time message feed
 * - @mention support with autocomplete
 * - Email notifications for mentions
 * - Message editing and deletion
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(17, 24, 39, 0.98);
  border-top: 1px solid rgba(75, 85, 99, 0.3);
`;

const Header = styled.div`
  padding: 12px 16px;
  background: rgba(31, 41, 55, 0.8);
  border-bottom: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const HeaderIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const HeaderSubtitle = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(75, 85, 99, 0.5);
    border-radius: 3px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease;
  
  &:hover {
    .message-actions {
      opacity: 1;
    }
  }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.$color || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: white;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const AuthorName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #e5e7eb;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

const EditedBadge = styled.span`
  font-size: 10px;
  color: #9ca3af;
  font-style: italic;
`;

const MessageText = styled.div`
  font-size: 13px;
  color: #d1d5db;
  line-height: 1.5;
  word-break: break-word;
  
  .mention {
    color: #60a5fa;
    background: rgba(96, 165, 250, 0.15);
    padding: 1px 4px;
    border-radius: 4px;
    font-weight: 500;
  }
`;

const MessageActions = styled.div`
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const ActionBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: rgba(55, 65, 81, 0.6);
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(99, 102, 241, 0.2)'};
    color: ${props => props.$danger ? '#f87171' : '#a5b4fc'};
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const InputArea = styled.div`
  padding: 12px 16px;
  border-top: 1px solid rgba(75, 85, 99, 0.3);
  background: rgba(31, 41, 55, 0.6);
  flex-shrink: 0;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const TextInput = styled.textarea`
  flex: 1;
  padding: 10px 14px;
  background: rgba(17, 24, 39, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.5);
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  min-height: 42px;
  max-height: 120px;
  line-height: 1.4;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const SendButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MentionPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 60px;
  margin-bottom: 4px;
  background: rgba(31, 41, 55, 0.98);
  border: 1px solid rgba(75, 85, 99, 0.5);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  max-height: 200px;
  overflow-y: auto;
  animation: ${slideIn} 0.15s ease;
  z-index: 10;
`;

const MentionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  
  ${props => props.$selected && css`
    background: rgba(99, 102, 241, 0.2);
  `}
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
  }
`;

const MentionAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: white;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MentionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MentionName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e5e7eb;
`;

const MentionTitle = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: #6b7280;
  
  svg {
    width: 40px;
    height: 40px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
  
  p {
    font-size: 13px;
    margin: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #6b7280;
  font-size: 12px;
`;

// ============================================
// ICONS
// ============================================

const Icons = {
  team: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  chat: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function formatTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return date.toLocaleDateString();
}

function renderMessageWithMentions(content) {
  // Replace @[Name](userId) with styled mention spans
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    parts.push(<span key={match.index} className="mention">@{match[1]}</span>);
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }
  
  return parts.length ? parts : content;
}

// ============================================
// MAIN COMPONENT
// ============================================

const TeamFeed = ({ threadId, conversationId, clientName }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!threadId) return;
    
    try {
      const response = await authenticatedAxios.get(`/case-notes/${threadId}`);
      if (response.data?.notes) {
        setMessages(response.data.notes);
      }
    } catch (error) {
      console.error('Error fetching case notes:', error);
    } finally {
      setLoading(false);
    }
  }, [threadId, authenticatedAxios]);

  // Fetch team members for mentions
  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await authenticatedAxios.get('/case-notes/team-members');
      if (response.data?.members) {
        setTeamMembers(response.data.members);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [authenticatedAxios]);

  useEffect(() => {
    fetchMessages();
    fetchTeamMembers();
  }, [fetchMessages, fetchTeamMembers]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle input change with mention detection
  const handleInputChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    setInputValue(value);
    setCursorPosition(position);
    
    // Check for @ mention trigger
    const textBeforeCursor = value.slice(0, position);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setMentionSearch(mentionMatch[1].toLowerCase());
      setShowMentionPopup(true);
      setSelectedMentionIndex(0);
    } else {
      setShowMentionPopup(false);
    }
  };

  // Filter team members based on search
  const filteredMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(mentionSearch)
  );

  // Insert mention
  const insertMention = (member) => {
    const textBeforeCursor = inputValue.slice(0, cursorPosition);
    const textAfterCursor = inputValue.slice(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const newTextBeforeCursor = textBeforeCursor.slice(0, mentionMatch.index);
      const mentionText = `@[${member.name}](${member.id}) `;
      const newValue = newTextBeforeCursor + mentionText + textAfterCursor;
      setInputValue(newValue);
      setShowMentionPopup(false);
      
      // Focus input and move cursor
      setTimeout(() => {
        inputRef.current?.focus();
        const newPosition = newTextBeforeCursor.length + mentionText.length;
        inputRef.current?.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  // Handle keyboard navigation in mention popup
  const handleKeyDown = (e) => {
    if (showMentionPopup && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(filteredMembers[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentionPopup(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Send message
  const handleSend = async () => {
    if (!inputValue.trim() || !threadId || sending) return;
    
    setSending(true);
    try {
      const response = await authenticatedAxios.post('/case-notes', {
        threadId,
        conversationId,
        clientName,
        content: inputValue.trim()
      });
      
      if (response.data?.note) {
        setMessages(prev => [...prev, response.data.note]);
        setInputValue('');
        
        // Check if there were mentions
        const mentions = response.data.note.mentions || [];
        if (mentions.length > 0) {
          showToast(`Note sent! ${mentions.length} team member(s) will be notified.`, 'success');
        }
      }
    } catch (error) {
      console.error('Error sending note:', error);
      showToast('Failed to send note', 'error');
    } finally {
      setSending(false);
    }
  };

  // Delete message
  const handleDelete = async (noteId) => {
    try {
      await authenticatedAxios.delete(`/case-notes/${noteId}`);
      setMessages(prev => prev.filter(m => m._id !== noteId));
      showToast('Note deleted', 'success');
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast('Failed to delete note', 'error');
    }
  };

  if (!threadId) {
    return (
      <Container>
        <Header>
          <HeaderIcon>{Icons.team}</HeaderIcon>
          <div>
            <HeaderTitle>Team Notes</HeaderTitle>
            <HeaderSubtitle>Internal communication</HeaderSubtitle>
          </div>
        </Header>
        <EmptyState>
          {Icons.chat}
          <p>Select a case to view team notes</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderIcon>{Icons.team}</HeaderIcon>
        <div>
          <HeaderTitle>Team Notes</HeaderTitle>
          <HeaderSubtitle>
            {clientName ? `Notes for ${clientName}` : 'Internal communication'}
          </HeaderSubtitle>
        </div>
      </Header>

      <MessagesContainer>
        {loading ? (
          <LoadingSpinner>Loading notes...</LoadingSpinner>
        ) : messages.length === 0 ? (
          <EmptyState>
            {Icons.chat}
            <p>No notes yet. Start the conversation!</p>
            <p style={{ fontSize: '11px', marginTop: '4px' }}>Use @mention to notify team members</p>
          </EmptyState>
        ) : (
          messages.map((message) => (
            <MessageWrapper key={message._id}>
              <Avatar>
                {message.authorAvatar ? (
                  <img src={message.authorAvatar} alt={message.authorName} />
                ) : (
                  getInitials(message.authorName)
                )}
              </Avatar>
              <MessageContent>
                <MessageHeader>
                  <AuthorName>{message.authorName}</AuthorName>
                  <Timestamp>{formatTime(message.createdAt)}</Timestamp>
                  {message.isEdited && <EditedBadge>(edited)</EditedBadge>}
                </MessageHeader>
                <MessageText>
                  {renderMessageWithMentions(message.content)}
                </MessageText>
              </MessageContent>
              <MessageActions className="message-actions">
                <ActionBtn $danger onClick={() => handleDelete(message._id)} title="Delete">
                  {Icons.trash}
                </ActionBtn>
              </MessageActions>
            </MessageWrapper>
          ))
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputArea>
        <InputWrapper>
          {showMentionPopup && filteredMembers.length > 0 && (
            <MentionPopup>
              {filteredMembers.map((member, index) => (
                <MentionItem 
                  key={member.id}
                  $selected={index === selectedMentionIndex}
                  onClick={() => insertMention(member)}
                >
                  <MentionAvatar>
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.name} />
                    ) : (
                      getInitials(member.name)
                    )}
                  </MentionAvatar>
                  <MentionInfo>
                    <MentionName>{member.name}</MentionName>
                    {member.title && <MentionTitle>{member.title}</MentionTitle>}
                  </MentionInfo>
                </MentionItem>
              ))}
            </MentionPopup>
          )}
          <TextInput
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Write a note... Use @ to mention team members"
            rows={1}
          />
          <SendButton onClick={handleSend} disabled={!inputValue.trim() || sending}>
            {Icons.send}
          </SendButton>
        </InputWrapper>
      </InputArea>
    </Container>
  );
};

export default TeamFeed;
